import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { useSession } from './lib/session';
import { CompanyLayout } from './layouts/CompanyLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { AccessTokenPage, ForgotPasswordPage, LoginPage, SelectCompanyPage, SignupPage } from './pages/auth/AuthPages';
import { LoadingRows } from './components/ui/States';
import { BrandMark } from './layouts/AppShell';

const DashboardPage = lazy(() => import('./pages/company/Dashboard'));
const EntriesPage = lazy(() => import('./pages/company/Entries'));
const ApprovalsPage = lazy(() => import('./pages/company/Approvals'));
const BanksPage = lazy(() => import('./pages/company/Banks'));
const BankAccountPage = lazy(() => import('./pages/company/BankAccount'));
const RegistryPage = lazy(() => import('./pages/company/Registry'));
const ReportsPage = lazy(() => import('./pages/company/Reports'));
const SettingsPage = lazy(() => import('./pages/company/Settings'));
const AdminOverviewPage = lazy(() => import('./pages/admin/Overview'));
const AdminTenantsPage = lazy(() => import('./pages/admin/Tenants'));
const AdminTenantDetailPage = lazy(() => import('./pages/admin/TenantDetail'));
const AdminPlansPage = lazy(() => import('./pages/admin/Plans'));
const AdminSubscriptionsPage = lazy(() => import('./pages/admin/Subscriptions'));
const AdminUsersPage = lazy(() => import('./pages/admin/Users'));
const AdminAuditPage = lazy(() => import('./pages/admin/Audit'));

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center" role="status" aria-label="Carregando">
      <div className="app-backdrop" aria-hidden />
      <BrandMark className="size-12 animate-pulse" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="card"><LoadingRows /></div>}>{children}</Suspense>;
}

/** Espera a sessão carregar antes de decidir rotas protegidas. */
function Gate({ children, requireAuth = true }: { children: ReactNode; requireAuth?: boolean }) {
  const { me, loading } = useSession();
  if (loading) return <Splash />;
  if (requireAuth && !me) return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

function Home() {
  const { me } = useSession();
  if (!me) return <Navigate to="/entrar" replace />;
  if (me.activeCompany) return <Navigate to="/app" replace />;
  if (me.memberships.length > 0) return <Navigate to="/empresas" replace />;
  if (me.platformRole) return <Navigate to="/admin" replace />;
  return <Navigate to="/app" replace />;
}

const page = (el: ReactNode) => <Lazy>{el}</Lazy>;

export const router = createBrowserRouter([
  { path: '/', element: <Gate><Home /></Gate> },
  { path: '/entrar', element: <Gate requireAuth={false}><LoginPage /></Gate> },
  { path: '/entrar/:empresa', element: <Gate requireAuth={false}><LoginPage /></Gate> },
  { path: '/cadastro', element: <Gate requireAuth={false}><SignupPage /></Gate> },
  { path: '/esqueci-senha', element: <ForgotPasswordPage /> },
  { path: '/acesso/:token', element: <AccessTokenPage /> },
  { path: '/empresas', element: <Gate><SelectCompanyPage /></Gate> },
  {
    path: '/app',
    element: <Gate><CompanyLayout /></Gate>,
    children: [
      { index: true, element: page(<DashboardPage />) },
      { path: 'pagar', element: page(<EntriesPage kind="payable" />) },
      { path: 'receber', element: page(<EntriesPage kind="receivable" />) },
      { path: 'aprovacoes', element: page(<ApprovalsPage />) },
      { path: 'bancos', element: page(<BanksPage />) },
      { path: 'bancos/:id', element: page(<BankAccountPage />) },
      { path: 'cadastros', element: page(<RegistryPage />) },
      { path: 'relatorios', element: page(<ReportsPage />) },
      { path: 'configuracoes', element: page(<SettingsPage />) },
    ],
  },
  {
    path: '/admin',
    element: <Gate><AdminLayout /></Gate>,
    children: [
      { index: true, element: page(<AdminOverviewPage />) },
      { path: 'empresas', element: page(<AdminTenantsPage />) },
      { path: 'empresas/:id', element: page(<AdminTenantDetailPage />) },
      { path: 'planos', element: page(<AdminPlansPage />) },
      { path: 'assinaturas', element: page(<AdminSubscriptionsPage />) },
      { path: 'usuarios', element: page(<AdminUsersPage />) },
      { path: 'auditoria', element: page(<AdminAuditPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
